import { RiskSetupEmptyState } from "@/components/setup-empty-state";
import { customDateRangeBounds } from "@/components/filters/filter-schema";
import { EnableLoggingOverlay } from "@/components/EnableLoggingOverlay";
import { IdentityLink } from "@/components/identity-link";
import { identityRefForUserKey } from "@/lib/identity-urn";
import { LogWorkbench } from "@/components/log-workbench";
import {
  defineFilters,
  useFilterState,
  type FilterValue,
} from "@/components/filters";
import { Page } from "@/components/page-layout";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { Checkbox } from "@/components/ui/Checkbox";
import { MoreActions, type Action } from "@/components/ui/MoreActions";
import { useOrganization } from "@/contexts/Auth";
import { useProjectSlugForRequests, useSdkClient } from "@/contexts/Sdk";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { useRowSelection, type RowSelection } from "@/hooks/useRowSelection";
import { useMeasuredHeight } from "@/hooks/useMeasuredHeight";
import { cn } from "@/lib/utils";
import { ChatDetailSheet } from "@/pages/chatLogs/ChatDetailPanel";
import { getPresetRange } from "@/elements";
import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import { useAssistantsList } from "@gram/client/react-query/assistantsList.js";
import { useProject } from "@/contexts/Auth";
import { useRBAC } from "@/hooks/useRBAC";
import { useRiskListPolicies } from "@gram/client/react-query/riskListPolicies.js";
import { useMcpServers } from "@gram/client/react-query/mcpServers.js";
import { useMetaMcpServers } from "@gram/client/react-query/metaMcpServers.js";
import { useListToolsets } from "@gram/client/react-query/listToolsets.js";
import { useProductFeatures } from "@gram/client/react-query/productFeatures.js";
import { useRiskMcpServerCounts } from "@gram/client/react-query/riskMcpServerCounts.js";
import { useRiskOverview } from "@gram/client/react-query/riskOverview.js";
import { useRiskListMcpPlatformToolsets } from "@gram/client/react-query/riskListMcpPlatformToolsets.js";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { History } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import {
  enforcementOutcomeLabel,
  isBlockingOutcome,
  mcpServerDisplayName,
} from "./risk-outcome";
import { chatFindingDetail } from "./finding-kind";
import { FindingDrawer } from "./FindingDrawer";
import { MCPFindingContext } from "./MCPFindingContext";
import {
  buildMCPFindingNames,
  isMCPFinding,
  type MCPFindingNames,
} from "./mcp-finding-context";
import { useDismissFinding } from "./useDismissFinding";
import { useSetupExclusionRule } from "./useSetupExclusionRule";
import { riskCategoryFilterOptions } from "./risk-category-options";
import {
  CategoryLabel,
  EventMatchDialog,
  MaskedMatch,
  RevealAllProvider,
  RevealAllToggle,
  RuleLabel,
} from "./risk-ui";
import {
  isRationaleSource,
  isShadowMcpSource,
  SEVERITY_RATING_LABEL,
} from "./risk-utils";
import {
  displayedScoreRating,
  SEVERITY_EDGE,
  SEVERITY_TEXT,
} from "./risk-severity";

// Signal-list layout (Risk Watchdog idiom): the severity score leads each row
// as a big serif numeral, so it sits directly after the checkbox.
//
// Category and rule share one column, badge over rule title, matching the
// findings table on the risk overview category drill-down. Judge findings own a
// single rule whose name only restates the category, so they render the badge
// alone rather than an empty Rule cell.
//
// Evidence gets the widest track: for judge and LLM analyzer findings it holds
// a sentence or two of rationale, where every other column holds a label. The
// actions track also holds the enforcement outcome of MCP findings.
const RISK_EVENTS_GRID =
  "grid grid-cols-[28px_80px_156px_minmax(0,1.2fr)_minmax(0,1.35fr)_minmax(0,0.9fr)_minmax(0,1.9fr)_minmax(0,0.9fr)_120px] gap-3";

// The signal-list score block: thin display-serif numeral over a mono
// uppercase severity word, both colored by band. The numeral carries the exact
// policy score while the word makes the band scannable.
function SignalScore({ score }: { score: number | undefined }): JSX.Element {
  if (score == null) {
    return <span className="text-muted-foreground text-sm">-</span>;
  }
  const { displayed, rating } = displayedScoreRating(score);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span
        className={cn(
          "font-display text-3xl leading-none font-thin tabular-nums",
          SEVERITY_TEXT[rating],
        )}
      >
        {displayed.toFixed(1)}
      </span>
      <span
        className={cn(
          "font-mono text-2xs tracking-widest uppercase",
          SEVERITY_TEXT[rating],
        )}
      >
        {SEVERITY_RATING_LABEL[rating]}
      </span>
    </div>
  );
}

// Strongly-typed filter schema for Risk Events. `policy_id` and the date range
// and the MCP server are pinned (always visible in the bar); the rest live
// behind "More filters".
// `listRiskResults` already accepts from/to, so the date range needs no backend
// change. (Source isn't a list param, so it's intentionally omitted here.)
const RISK_FILTERS = defineFilters([
  { id: "policy_id", label: "Policy", kind: "select", pinned: true },
  { id: "date", label: "Date range", kind: "daterange", pinned: true },
  { id: "mcp_server_id", label: "MCP server", kind: "select", pinned: true },
  // The category the finding was classified into at ingest (Secrets, Shadow
  // AI, ...): the one axis that cuts across policies and rules.
  { id: "category", label: "Category", kind: "select", pinned: true },
  {
    id: "rule_id",
    label: "Rule ID",
    kind: "text",
    placeholder: "Rule ID contains...",
  },
  {
    id: "user_id",
    label: "User",
    kind: "text",
    placeholder: "User contains...",
  },
  // Whole external user ids, set by an identity's "Open in Risk Events" so a
  // person known by several ids lands on all of their events and nobody
  // else's. Not offered for picking: its only options are the ids it holds.
  { id: "identifier", label: "Identifier", kind: "multiselect" },
  {
    id: "unique",
    label: "Unique matches only",
    kind: "boolean",
    description: "Keep the latest row per policy, rule and matched value.",
  },
  { id: "assistant", label: "Assistant", kind: "select" },
]);

// The per-server counts endpoint rejects windows longer than this.
const MAX_COUNTS_WINDOW_MS = 31 * 24 * 60 * 60 * 1000;

// Sentinel option value for the assistant filter meaning "chats with no
// assistant link" — maps to the API's non_assistant flag rather than an
// assistant_id. Assistant ids are UUIDs, so this can't collide.
const NO_ASSISTANT = "none";

export default function RiskEvents(): JSX.Element {
  const project = useProject();
  const { hasScope } = useRBAC();
  const canReadAssistants = hasScope("assistant:read", undefined, project.id);
  const client = useSdkClient();
  const gramProject = useProjectSlugForRequests();
  const organization = useOrganization();
  const featuresQuery = useProductFeatures({
    organizationId: organization.id,
  });
  const isLoggingDisabled =
    !featuresQuery.isPending &&
    !featuresQuery.isError &&
    featuresQuery.data?.logsEnabled === false;
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedChatId = searchParams.get("chat_id");
  const selectedFindingId = searchParams.get("finding");
  const containerRef = useRef<HTMLDivElement>(null);
  const headerMeasure = useMeasuredHeight<HTMLDivElement>();

  const { values, setValue, clearValue, clearAll } =
    useFilterState(RISK_FILTERS);
  const policyFilter = values.policy_id ?? "";
  const mcpServerFilter = values.mcp_server_id ?? "";
  const categoryFilter = values.category ?? "";
  const ruleFilter = values.rule_id;
  const userFilter = values.user_id;
  const identifierFilter = values.identifier;
  // A stable dependency: the array is rebuilt whenever any param changes,
  // including opening a row.
  const identifierKey = identifierFilter.join(",");
  const uniqueOnly = values.unique;
  // "No assistant" pre-selects the non-assistant events (the API's
  // non_assistant flag); any other value scopes to that assistant's chats.
  const assistantFilter = values.assistant ?? "";
  const nonAssistantOnly = assistantFilter === NO_ASSISTANT;
  const assistantId =
    assistantFilter && assistantFilter !== NO_ASSISTANT
      ? assistantFilter
      : undefined;

  // The date range maps to the endpoint's from/to. A null preset with no custom
  // range means "all time" (no from/to sent) — Risk Events' previous behavior.
  const { from, to } = useMemo(() => {
    const d = values.date;
    const customRange = customDateRangeBounds(d);
    if (customRange) return customRange;
    if (d.preset) return getPresetRange(d.preset);
    return { from: undefined, to: undefined };
  }, [values.date]);

  const setSelectedChatId = useCallback(
    (chatId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (chatId) {
            next.set("chat_id", chatId);
          } else {
            next.delete("chat_id");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSelectedFindingId = useCallback(
    (findingId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (findingId) {
            next.set("finding", findingId);
          } else {
            next.delete("finding");
          }
          // The transcript belongs to the finding it was opened from.
          next.delete("chat_id");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: policiesData, isLoading: policiesLoading } =
    useRiskListPolicies();
  const policies = useMemo(
    () => policiesData?.policies ?? [],
    [policiesData?.policies],
  );

  const mcpScopedFlag = useFeatureFlag(FEATURE_FLAGS.mcpScopedPolicies);
  const mcpScoped = mcpScopedFlag.status === "enabled";

  const { data: mcpServersData } = useMcpServers({ gramProject }, undefined, {
    throwOnError: false,
  });
  const mcpServers = useMemo(
    () => mcpServersData?.mcpServers ?? [],
    [mcpServersData?.mcpServers],
  );
  // Per-server finding counts for the picker, for the selected range. The
  // endpoint rejects windows over 31 days, so longer or open-ended ranges show
  // the servers without counts rather than counts for a different range.
  const countsWindow = useMemo(() => {
    if (!from) return null;
    const end = to ?? new Date();
    if (end.getTime() - from.getTime() > MAX_COUNTS_WINDOW_MS) return null;
    return { from, to: end };
  }, [from, to]);
  const { data: serverCountsData } = useRiskMcpServerCounts(
    countsWindow ?? {},
    undefined,
    { throwOnError: false, enabled: mcpScoped && countsWindow !== null },
  );
  const findingsByServer = useMemo(
    () =>
      new Map(
        (serverCountsData?.servers ?? []).map((row) => [
          row.mcpServerId,
          row.findings,
        ]),
      ),
    [serverCountsData?.servers],
  );

  const { data: platformToolsetsData } = useRiskListMcpPlatformToolsets(
    { gramProject },
    undefined,
    { throwOnError: false },
  );
  const platformToolsets = useMemo(
    () => platformToolsetsData?.toolsets ?? [],
    [platformToolsetsData?.toolsets],
  );
  const { data: metaMcpServersData } = useMetaMcpServers(
    { gramProject },
    undefined,
    { throwOnError: false },
  );
  const metaMcpServers = useMemo(
    () => metaMcpServersData?.metaMcpServers ?? [],
    [metaMcpServersData?.metaMcpServers],
  );
  const { data: toolsetsData } = useListToolsets({ gramProject }, undefined, {
    throwOnError: false,
  });
  const toolsets = useMemo(
    () => toolsetsData?.toolsets ?? [],
    [toolsetsData?.toolsets],
  );
  const mcpFindingNames = useMemo(
    () => buildMCPFindingNames([...mcpServers, ...metaMcpServers], toolsets),
    [mcpServers, metaMcpServers, toolsets],
  );
  // Powers the rule_id filter autocomplete: surface only rules that actually
  // have findings in this project's recent window.
  const { data: overviewData } = useRiskOverview({}, undefined, {
    throwOnError: false,
  });
  const ruleSuggestions = useMemo(
    () => (overviewData?.topRules ?? []).map((r) => r.ruleId).filter(Boolean),
    [overviewData?.topRules],
  );
  // The default view includes disabled policies' historical findings, so their
  // rows carry the same "(inactive)" label as the policy filter.
  const policyNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const policy of policies) {
      if (policy.name && policy.name.trim() !== "") {
        m.set(
          policy.id,
          policy.enabled === false ? `${policy.name} (inactive)` : policy.name,
        );
      }
    }
    return m;
  }, [policies]);

  // Findings surface their owning policy's severity score, resolved by
  // risk_policy_id at read time (score is a policy attribute, not stored on the
  // finding).
  const policyScoreById = useMemo(() => {
    const m = new Map<string, number>();
    for (const policy of policies) {
      m.set(policy.id, policy.score);
    }
    return m;
  }, [policies]);

  // The policy currently selected in the filter, if any. When it's disabled the
  // list returns only its historical findings, so we surface a notice that the
  // user is viewing data for an inactive policy.
  const selectedPolicy = useMemo(
    () => policies.find((p) => p.id === policyFilter),
    [policies, policyFilter],
  );
  const viewingInactivePolicy =
    selectedPolicy != null && selectedPolicy.enabled === false;

  // Powers the assistant filter options; "No assistant" is always offered so
  // findings missing user attribution can be surfaced even before any
  // assistant exists in the project.
  const { data: assistantsData } = useAssistantsList(
    { gramProject: project.slug },
    undefined,
    {
      enabled: canReadAssistants,
      throwOnError: false,
    },
  );
  const assistants = useMemo(
    () => (canReadAssistants ? (assistantsData?.assistants ?? []) : []),
    [assistantsData?.assistants, canReadAssistants],
  );

  // Page-supplied option lists for the schema's select/text dimensions.
  // Disabled policies stay selectable — they hold historical findings — but are
  // labelled "(inactive)" so the distinction is clear in the dropdown.
  const filterOptions = useMemo(
    () => ({
      policy_id: policies.map((p) => ({
        label: p.enabled === false ? `${p.name} (inactive)` : p.name,
        value: p.id,
      })),
      mcp_server_id: [
        ...mcpServers.map((server) => {
          const findings = findingsByServer.get(server.id);
          const name = mcpServerDisplayName(server);
          return {
            label:
              mcpScoped && findings != null
                ? `${name} · ${findings.toLocaleString()} findings`
                : name,
            value: server.id,
          };
        }),
        ...platformToolsets.map((toolset) => ({
          label: `${toolset.name} (Platform MCP)`,
          value: toolset.id,
        })),
      ],
      category: riskCategoryFilterOptions(),
      rule_id: ruleSuggestions.map((r) => ({ label: r, value: r })),
      identifier: identifierFilter.map((id) => ({ label: id, value: id })),
      assistant: [
        { label: "No assistant", value: NO_ASSISTANT },
        ...assistants.map((a) => ({ label: a.name, value: a.id })),
      ],
    }),
    [
      policies,
      mcpServers,
      platformToolsets,
      mcpScoped,
      findingsByServer,
      ruleSuggestions,
      assistants,
      identifierFilter,
    ],
  );

  const fromIso = from?.toISOString();
  const toIso = to?.toISOString();

  // Reset the virtualized list to the top whenever a filter changes, so users
  // don't stay at a stale offset and miss the newly filtered results.
  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 });
  }, [
    policyFilter,
    mcpServerFilter,
    categoryFilter,
    ruleFilter,
    userFilter,
    identifierKey,
    uniqueOnly,
    assistantFilter,
    fromIso,
    toIso,
  ]);

  const resultsQuery = useInfiniteQuery({
    queryKey: [
      "risk",
      "results",
      "list",
      policyFilter,
      mcpServerFilter,
      categoryFilter,
      ruleFilter,
      userFilter,
      identifierKey,
      uniqueOnly,
      assistantFilter,
      fromIso,
      toIso,
    ],
    queryFn: async ({ pageParam }) => {
      return client.risk.results.list({
        cursor: pageParam,
        limit: 50,
        policyId: policyFilter || undefined,
        mcpServerId: mcpServerFilter || undefined,
        category: categoryFilter || undefined,
        ruleId: ruleFilter || undefined,
        userId: userFilter || undefined,
        externalUserIds:
          identifierFilter.length > 0 ? identifierFilter : undefined,
        uniqueMatch: uniqueOnly || undefined,
        nonAssistant: nonAssistantOnly || undefined,
        assistantId,
        from,
        to,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const results = useMemo(
    () => resultsQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [resultsQuery.data],
  );
  const totalCount = resultsQuery.data?.pages[0]?.totalCount ?? results.length;
  const isInitialLoading = policiesLoading || resultsQuery.isLoading;

  const { dismiss, isOptimisticallyDismissed } = useDismissFinding();
  const visibleResults = useMemo(
    () => results.filter((r) => !isOptimisticallyDismissed(r.id)),
    [results, isOptimisticallyDismissed],
  );
  const selection = useRowSelection(visibleResults, (r) => r.id);
  const handleDismissSelected = useCallback(() => {
    const toDismiss = selection.selectedItems;
    if (toDismiss.length === 0) return;
    dismiss(toDismiss);
    selection.clear();
  }, [selection, dismiss]);

  const exclusionRule = useSetupExclusionRule();
  const handleSetupExclusionSelected = useCallback(() => {
    const selected = selection.selectedItems;
    if (selected.length === 0) return;
    // Deliberately doesn't clear the selection (unlike handleDismissSelected)
    // so retrying still works if the sheet is canceled.
    exclusionRule.open(selected);
  }, [selection, exclusionRule]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const distanceFromBottom =
        container.scrollHeight - (container.scrollTop + container.clientHeight);

      if (resultsQuery.isFetchingNextPage || resultsQuery.isFetching) return;
      if (!resultsQuery.hasNextPage) return;

      if (distanceFromBottom < 200) {
        void resultsQuery.fetchNextPage();
      }
    },
    [resultsQuery],
  );

  if (isLoggingDisabled) {
    return (
      <LogWorkbench
        eyebrow="Secure"
        title="Risk Events"
        description="Review policy findings across recent analyzed chats."
      >
        <div>
          <EnableLoggingOverlay
            onEnabled={() => {
              void featuresQuery.refetch();
              void resultsQuery.refetch();
            }}
            screenshotSrc="/empty-states/risk_events_empty.png"
            screenshotAlt="Risk Events dashboard with policy findings"
            className="border-0"
          />
        </div>
      </LogWorkbench>
    );
  }

  return (
    <RevealAllProvider>
      <LogWorkbench
        eyebrow="Security and Policy"
        title="Risk Events"
        description="Review policy findings across recent analyzed chats."
        actions={
          <RevealAllToggle
            // Match the "More filters"/"Refresh" toolbar triggers (hairline
            // secondary button, h-10, mono label) instead of the component's
            // hand-rolled default.
            className="border-neutral-default text-btn-secondary hover:bg-btn-secondary hover:text-btn-secondary-hover inline-flex h-10 items-center gap-2 border bg-transparent px-4 font-mono text-sm tracking-[-0.01em] transition-all"
          />
        }
        filters={
          <Page.Toolbar>
            <Page.Toolbar.Filters
              schema={RISK_FILTERS}
              values={values}
              optionsById={filterOptions}
              onChange={setValue as (id: string, value: FilterValue) => void}
              onClear={clearValue as (id: string) => void}
              onClearAll={clearAll}
            />
            <Page.Toolbar.Refresh
              onRefresh={() => void resultsQuery.refetch()}
              isRefreshing={resultsQuery.isFetching}
            />
          </Page.Toolbar>
        }
        status={
          <>
            {viewingInactivePolicy ? (
              <InactivePolicyNotice policyName={selectedPolicy?.name} />
            ) : null}
            {exclusionRule.sheet}
            {resultsQuery.isFetching && results.length > 0 ? (
              <div className="bg-primary/20 h-1 shrink-0">
                <div className="bg-primary h-full animate-pulse" />
              </div>
            ) : null}
          </>
        }
        header={
          <div className="relative min-w-[1200px]">
            <BulkActionBar
              selectedCount={selection.selectedCount}
              actions={[
                {
                  label: "Suppress Once",
                  onClick: handleDismissSelected,
                },
                {
                  label: "Create Rule",
                  onClick: handleSetupExclusionSelected,
                },
              ]}
              leftOffsetPx={28}
              heightPx={headerMeasure.height}
            />
            <RiskEventsHeader
              selection={selection}
              headerRef={headerMeasure.ref}
            />
          </div>
        }
        footer={
          results.length > 0 ? (
            <RiskEventsFooter
              count={results.length}
              totalCount={totalCount}
              hasNextPage={resultsQuery.hasNextPage}
              isFetchingNextPage={resultsQuery.isFetchingNextPage}
              onLoadMore={() => {
                void resultsQuery.fetchNextPage();
              }}
            />
          ) : null
        }
        detail={
          <>
            <FindingDrawer
              findingId={selectedFindingId}
              results={visibleResults}
              policyNameById={policyNameById}
              policyScoreById={policyScoreById}
              mcpFindingNames={mcpFindingNames}
              transcriptChatId={selectedChatId}
              onSelect={setSelectedFindingId}
              onOpenTranscript={setSelectedChatId}
              onDismiss={(r) => dismiss([r])}
              onSetupExclusion={(r) => exclusionRule.open([r])}
            />
            {/* Older links open a transcript straight from the list; with a
                finding open the drawer hosts the transcript instead. */}
            <ChatDetailSheet
              chatId={selectedFindingId ? null : selectedChatId}
              onClose={() => setSelectedChatId(null)}
              onDelete={() => setSelectedChatId(null)}
              riskFocus
            />
          </>
        }
        scrollRef={containerRef}
        onScroll={handleScroll}
        surfaceClassName="overflow-x-auto"
        contentClassName="min-w-[1200px]"
      >
        <RiskEventsRows
          error={resultsQuery.error}
          isLoading={isInitialLoading}
          filtered={
            !!(
              policyFilter ||
              mcpServerFilter ||
              ruleFilter ||
              userFilter ||
              uniqueOnly ||
              assistantFilter ||
              from ||
              to
            )
          }
          results={visibleResults}
          policyNameById={policyNameById}
          policyScoreById={policyScoreById}
          mcpFindingNames={mcpFindingNames}
          scrollRef={containerRef}
          selectedFindingId={selectedFindingId}
          onSelectFinding={setSelectedFindingId}
          selection={selection}
          onDismiss={(r) => dismiss([r])}
          onSetupExclusion={(r) => exclusionRule.open([r])}
        />
      </LogWorkbench>
    </RevealAllProvider>
  );
}

// Shown when the active policy filter points at a disabled ("turned off")
// policy. Those policies no longer produce new findings, so the list is purely
// historical — make that explicit rather than leaving users to assume the data
// is current.
function InactivePolicyNotice({
  policyName,
}: {
  policyName: string | undefined;
}) {
  return (
    <div className="text-warning flex shrink-0 items-center gap-2 border-b px-5 py-2 text-sm">
      <History className="size-4 shrink-0" />
      <span>
        {policyName ? (
          <>
            <span className="font-medium">{policyName}</span> is no longer
            active.
          </>
        ) : (
          "This policy is no longer active."
        )}{" "}
        You're viewing historical findings from when it was enabled.
      </span>
    </div>
  );
}

function RiskEventsHeader({
  selection,
  headerRef,
}: {
  selection: RowSelection<RiskResult>;
  headerRef: (node: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={headerRef}
      className={cn(
        RISK_EVENTS_GRID,
        // whitespace-nowrap keeps two-word labels ("Session Name") on one
        // line when their fr track compresses.
        "text-eyebrow bg-muted/30 shrink-0 items-center border-b px-5 py-2.5 whitespace-nowrap",
      )}
    >
      <div className="min-w-0">
        <Checkbox
          checked={selection.allState}
          onCheckedChange={() => selection.toggleAll()}
          aria-label="Select all loaded findings"
        />
      </div>
      <div className="min-w-0">Severity</div>
      <div className="min-w-0">Timestamp</div>
      <div className="min-w-0">Category / Rule</div>
      <div className="min-w-0 whitespace-nowrap">Session · Tool</div>
      <div className="min-w-0">User</div>
      <div className="min-w-0">Evidence</div>
      <div className="min-w-0">Policy</div>
      <div className="flex min-w-0 justify-center">Actions</div>
    </div>
  );
}

function RiskEventsRows({
  error,
  filtered,
  isLoading,
  results,
  policyNameById,
  policyScoreById,
  mcpFindingNames,
  scrollRef,
  selectedFindingId,
  onSelectFinding,
  selection,
  onDismiss,
  onSetupExclusion,
}: {
  error: Error | null;
  filtered: boolean;
  isLoading: boolean;
  results: RiskResult[];
  policyNameById: Map<string, string>;
  mcpFindingNames: MCPFindingNames;
  policyScoreById: Map<string, number>;
  scrollRef: RefObject<HTMLDivElement | null>;
  selectedFindingId: string | null;
  onSelectFinding: (findingId: string | null) => void;
  selection: RowSelection<RiskResult>;
  onDismiss: (result: RiskResult) => void;
  onSetupExclusion: (result: RiskResult) => void;
}) {
  const rowVirtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => scrollRef.current,
    // Rows stack category over rule and can wrap two lines of rationale;
    // measureElement corrects each row, this is just the pre-measure estimate.
    estimateSize: () => 68,
    overscan: 12,
  });

  // Keeps the drawer's j/k selection on screen.
  const selectedIndex = selectedFindingId
    ? results.findIndex((r) => r.id === selectedFindingId)
    : -1;
  useEffect(() => {
    if (selectedIndex >= 0) {
      rowVirtualizer.scrollToIndex(selectedIndex, { align: "auto" });
    }
  }, [selectedIndex, rowVirtualizer]);

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="bg-destructive/10 flex size-12 items-center justify-center">
          <Icon name="circle-alert" className="text-destructive size-6" />
        </div>
        <span className="text-foreground font-medium">
          Error loading risk events
        </span>
        <span className="text-muted-foreground max-w-sm text-center text-sm">
          {error.message}
        </span>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-12">
        <Icon name="loader-circle" className="size-5 animate-spin" />
        <span>Loading risk events...</span>
      </div>
    );
  }

  if (results.length === 0) {
    return <RiskSetupEmptyState filtered={filtered} />;
  }

  return (
    <div
      className="relative w-full"
      style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
        const result = results[virtualRow.index];
        if (!result) return null;

        return (
          <div
            key={result.id}
            ref={rowVirtualizer.measureElement}
            data-index={virtualRow.index}
            className="absolute top-0 left-0 w-full"
            style={{ transform: `translateY(${virtualRow.start}px)` }}
          >
            <RiskEventsRow
              result={result}
              policyName={policyNameById.get(result.policyId)}
              policyScore={policyScoreById.get(result.policyId)}
              mcpFindingNames={mcpFindingNames}
              selected={result.id === selectedFindingId}
              onSelect={onSelectFinding}
              selection={selection}
              onDismiss={onDismiss}
              onSetupExclusion={onSetupExclusion}
            />
          </div>
        );
      })}
    </div>
  );
}

export function RiskEventsRow({
  result,
  policyName,
  policyScore,
  mcpFindingNames,
  selected = false,
  onSelect,
  selection,
  onDismiss,
  onSetupExclusion,
}: {
  result: RiskResult;
  policyName: string | undefined;
  policyScore: number | undefined;
  mcpFindingNames?: MCPFindingNames;
  selected?: boolean;
  onSelect: (findingId: string) => void;
  selection: RowSelection<RiskResult>;
  onDismiss: (result: RiskResult) => void;
  onSetupExclusion: (result: RiskResult) => void;
}): JSX.Element {
  const outcomeLabel = enforcementOutcomeLabel(result.enforcementOutcome);
  const isShadowMCP = isShadowMcpSource(result.source);
  // Judge and LLM analyzer findings carry their evidence as a rationale.
  const isEventSource = isRationaleSource(result.source);
  // The 2px left edge carries the severity band color; rows whose policy
  // hasn't loaded a score keep a transparent edge so the grid stays aligned.
  const edgeRating =
    policyScore != null ? displayedScoreRating(policyScore).rating : null;

  // A row click opens the drawer only when the gesture both starts and ends inside
  // the row. This rejects the stray click Radix's outside-dismiss sends here:
  // closing the View-event dialog by clicking its overlay fires pointerdown on
  // the (portaled) overlay, which unmounts, so the trailing click lands on a row
  // cell and would otherwise open the drawer.
  const pointerDownInsideRef = useRef(false);

  const handleShare = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set("finding", result.id);
    url.searchParams.delete("chat_id");
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  }, [result.id]);

  const rowActions: Action[] = [
    { label: "Copy link", onClick: () => void handleShare() },
    { label: "Suppress Once", onClick: () => onDismiss(result) },
    { label: "Create Rule", onClick: () => onSetupExclusion(result) },
  ];

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected || undefined}
      className={cn(
        RISK_EVENTS_GRID,
        "w-full cursor-pointer items-center border-b border-l-2 px-5 py-3 text-left text-sm transition-colors",
        selected ? "bg-muted/50" : "hover:bg-muted/30",
        edgeRating ? SEVERITY_EDGE[edgeRating] : "border-l-transparent",
      )}
      onPointerDown={(e) => {
        pointerDownInsideRef.current = e.currentTarget.contains(
          e.target as Node,
        );
      }}
      onClick={(e) => {
        const startedInside = pointerDownInsideRef.current;
        pointerDownInsideRef.current = false;
        // Ignore the trailing click from an outside-dismiss (pointerdown never
        // landed on the row).
        if (!startedInside) return;
        // The row wraps its own interactive controls (match reveal, the
        // View-event dialog trigger, copy-link); a click on one of those must
        // not also open the drawer. stopPropagation on those children doesn't
        // reliably stop this handler under React's event delegation, so guard on
        // the real target too.
        if ((e.target as HTMLElement).closest("button, a")) return;
        onSelect(result.id);
      }}
      onKeyDown={(e) => {
        // Only the row itself activates on Enter/Space. Key events bubbling up
        // from a focused child control (match reveal, the event dialog trigger,
        // copy-link) must reach that control instead — preventing them here
        // would swallow the control's own activation and wrongly open the drawer.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(result.id);
        }
      }}
    >
      <div className="min-w-0">
        <Checkbox
          checked={selection.isSelected(result.id)}
          onCheckedChange={() => selection.toggle(result.id)}
          aria-label="Select finding"
        />
      </div>
      <div className="min-w-0">
        <SignalScore score={policyScore} />
      </div>
      <div className="text-muted-foreground min-w-0 font-mono text-xs">
        {result.createdAt ? new Date(result.createdAt).toLocaleString() : "-"}
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <CategoryLabel source={result.source} ruleId={result.ruleId} />
        <RuleLabel source={result.source} ruleId={result.ruleId} />
      </div>
      <div className="text-muted-foreground min-w-0 font-mono text-xs">
        {isMCPFinding(result) ? (
          <MCPFindingContext finding={result} names={mcpFindingNames} />
        ) : (
          <ChatFindingContext result={result} />
        )}
      </div>
      <div className="text-muted-foreground min-w-0 truncate font-mono text-xs">
        <IdentityLink identifier={identityRefForUserKey(result.userId)}>
          {result.userId ?? "-"}
        </IdentityLink>
      </div>
      {/* Judge rationale wraps to two lines, so this cell can't clip to one. */}
      <div className={cn("min-w-0", !isEventSource && "truncate")}>
        {isShadowMCP && result.matchRedacted ? (
          // Square hairline mono tag — the fingerprint reads as a chip, not
          // prose.
          <span
            className="border-border inline-block max-w-full truncate border px-1.5 py-0.5 font-mono text-xs"
            title={result.matchRedacted}
          >
            {result.matchRedacted}
          </span>
        ) : isEventSource ? (
          <EventMatchDialog
            resultId={result.id}
            matchRedacted={result.matchRedacted}
            rationale={result.description}
          />
        ) : (
          <MaskedMatch
            resultId={result.id}
            matchRedacted={result.matchRedacted}
          />
        )}
      </div>
      <div
        className="text-muted-foreground min-w-0 truncate font-mono text-xs"
        title={policyName}
      >
        {policyName ?? "-"}
      </div>
      <div
        className="flex min-w-0 items-center justify-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {outcomeLabel ? (
          <span
            className={cn(
              "truncate font-mono text-xs",
              isBlockingOutcome(result.enforcementOutcome)
                ? "text-foreground font-medium"
                : "text-muted-foreground",
            )}
            title={outcomeLabel}
          >
            {outcomeLabel}
          </span>
        ) : null}
        <MoreActions actions={rowActions} />
      </div>
    </div>
  );
}

function ChatFindingContext({ result }: { result: RiskResult }): JSX.Element {
  const title = result.chatTitle ?? "Untitled";
  const detail = chatFindingDetail(result);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-foreground block truncate" title={title}>
        {title}
      </span>
      {detail && (
        <span className="block truncate" title={detail}>
          {detail}
        </span>
      )}
    </div>
  );
}

function RiskEventsFooter({
  count,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  count: number;
  totalCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className="bg-muted/30 text-muted-foreground flex shrink-0 items-center justify-between gap-4 border-t px-5 py-3 text-sm">
      <span>
        Showing {count.toLocaleString()} of {totalCount.toLocaleString()}{" "}
        {totalCount === 1 ? "finding" : "findings"}
        {hasNextPage && " - Scroll to load more"}
      </span>
      {hasNextPage ? (
        <Button
          variant="tertiary"
          size="sm"
          disabled={isFetchingNextPage}
          onClick={onLoadMore}
        >
          {isFetchingNextPage ? "Loading..." : "Load More"}
        </Button>
      ) : null}
    </div>
  );
}
